import { arrayWithoutSubset, updateRecord, updateRecordWhere } from '@ms/yammer-data-support';

import { NestedThreadLoadRejectedAction } from '../../domains/nestedThread/types';
import {
  PublisherContentChangedAction,
  PublisherLinkModalClosedAction,
  PublisherLinkModalOpenedAction,
  PublisherPollOptionChangedAction,
  PublisherPostTypeChangedAction,
  PublisherPraiseIconChangedAction,
  PublisherPraisedUsersAddedAction,
  PublisherPraisedUsersChangedAction,
  PublisherPraisedUsersRemovedAction,
  PublisherTitleChangedAction,
} from '../../domains/publishers/actions.types';
import { ThreadLoadRejectedAction } from '../../domains/threadLoad/actions.types';
import { Publishers, isUserThreadStarterPublisher } from '../types';

import { PublishersAction } from './reducer.types';
import {
  Publisher,
  PublisherPostType,
  isNetworkQuestionNewQuestionPublisher,
  isPollPublisher,
  isPraisePublisher,
} from './types';

const openLinkModal = (state: Publishers, { id }: PublisherLinkModalOpenedAction['payload']) =>
  updateRecord(state, id, { isLinkModalOpen: true });

const closeLinkModal = (state: Publishers, { id }: PublisherLinkModalClosedAction['payload']) =>
  updateRecord(state, id, { isLinkModalOpen: false });

const emptyOption = '';
const maxPollOptions = 100;
const maxPollOptionLength = 1000;
const isPollOptionEmpty = (pollOption: string) => pollOption.trim() === '';
const shouldAddEmptyOption = (pollOptions: string[]) => {
  const canAddOption = pollOptions.length < maxPollOptions;
  const hasFilledAllOptions = pollOptions.every((pollOption: string) => !isPollOptionEmpty(pollOption));

  return canAddOption && hasFilledAllOptions;
};
const isValidPollContentLength = (pollOptionContent: string) => pollOptionContent.length <= maxPollOptionLength;
const changePollOption = (state: Publishers, payload: PublisherPollOptionChangedAction['payload']) => {
  const { id, pollOptionIndex, pollOptionContent } = payload;

  const previousPublisher = state[id];
  if (!previousPublisher || !isPollPublisher(previousPublisher)) {
    return state;
  }

  let pollOptions = [...previousPublisher.pollOptions];
  if (isValidPollContentLength(pollOptionContent)) {
    pollOptions = Object.assign([...pollOptions], { [pollOptionIndex]: pollOptionContent });
  }

  if (shouldAddEmptyOption(pollOptions)) {
    pollOptions = [...pollOptions, emptyOption];
  }

  return {
    ...state,
    [id]: {
      ...previousPublisher,
      pollOptions,
    },
  };
};

const addPraisedUsersToPublisher = (state: Publishers, payload: PublisherPraisedUsersAddedAction['payload']) => {
  const { id, userIds } = payload;

  const previousPublisher = state[id];
  if (!previousPublisher || !isPraisePublisher(previousPublisher)) {
    return state;
  }

  const uniquePraisedUserIds = Array.from(new Set([...previousPublisher.praisedUserIds, ...userIds]));

  return {
    ...state,
    [id]: {
      ...previousPublisher,
      praisedUserIds: uniquePraisedUserIds,
    },
  };
};

const removePraisedUsersFromPublisher = (
  state: Publishers,
  { id, userIds }: PublisherPraisedUsersRemovedAction['payload']
) => {
  const previousPublisher = state[id];
  if (!previousPublisher || !isPraisePublisher(previousPublisher)) {
    return state;
  }

  const praisedUserIds = arrayWithoutSubset(previousPublisher.praisedUserIds, userIds);

  return {
    ...state,
    [id]: {
      ...previousPublisher,
      praisedUserIds,
    },
  };
};

const changePraisedUsersFromPublisher = (
  state: Publishers,
  { id, userIds }: PublisherPraisedUsersChangedAction['payload']
) => {
  const previousPublisher = state[id];
  if (!previousPublisher || !isPraisePublisher(previousPublisher)) {
    return state;
  }

  return {
    ...state,
    [id]: {
      ...previousPublisher,
      praisedUserIds: userIds,
    },
  };
};

const changePraiseIconFromPublisher = (
  state: Publishers,
  { publisherId, badge }: PublisherPraiseIconChangedAction['payload']
) => {
  const previousPublisher = state[publisherId];
  if (!previousPublisher || !isPraisePublisher(previousPublisher)) {
    return state;
  }

  return {
    ...state,
    [publisherId]: {
      ...previousPublisher,
      badge,
      badgePanelOpen: false,
    },
  };
};

const updatePublisherPostType = (state: Publishers, payload: PublisherPostTypeChangedAction['payload']) => {
  const { id, postType } = payload;

  const previousPublisher = state[id];
  if (!previousPublisher) {
    return state;
  }

  const shouldUseUpdatePostType = previousPublisher.postType === postType;
  const updatePublisherPostType = isUserThreadStarterPublisher(previousPublisher)
    ? PublisherPostType.UserNewUpdate
    : PublisherPostType.GroupNewUpdate;
  const newPostType = shouldUseUpdatePostType ? updatePublisherPostType : postType;

  const fieldToUpdate: Pick<Publisher, 'postType'> = { postType: newPostType };
  const fieldToUpdateAsPartial = fieldToUpdate as Partial<Publisher>;

  return updateRecord(state, id, fieldToUpdateAsPartial);
};

const updateContent = (state: Publishers, payload: PublisherContentChangedAction['payload']): Publishers => {
  const { contentState, id } = payload;
  if (!state[id]) {
    return state;
  }

  const publisher = state[id];
  const currentContentState = publisher.contentState;

  if (isNetworkQuestionNewQuestionPublisher(publisher) && currentContentState !== contentState) {
    return updateRecord(state, id, {
      contentState,
      hasCollapsedRelatedQuestionsModule: true,
    });
  }

  return updateRecord(state, id, { contentState });
};

const updateTitle = (state: Publishers, payload: PublisherTitleChangedAction['payload']): Publishers => {
  const { title, id } = payload;
  if (!state[id]) {
    return state;
  }

  return updateRecord(state, id, { title, hasCollapsedRelatedQuestionsModule: false });
};

const removeFailedSharedThread = (state: Publishers, payload: ThreadLoadRejectedAction['payload']): Publishers => {
  const threadId = payload.id;

  return updateRecordWhere(state, (publisher) => publisher.sharedThreadId === threadId, { sharedThreadId: undefined });
};

const removeFailedSharedNestedThread = (
  state: Publishers,
  payload: NestedThreadLoadRejectedAction['payload']
): Publishers => {
  const threadId = payload.threadId;

  return updateRecordWhere(state, (publisher) => publisher.sharedThreadId === threadId, { sharedThreadId: undefined });
};

const toggleRelatedQuestionsModule = (state: Publishers, publisherId: Publisher['id']) => {
  const publisher = state[publisherId];

  if (!isNetworkQuestionNewQuestionPublisher(publisher)) {
    return state;
  }

  const updatedPublisherFields = {
    hasCollapsedRelatedQuestionsModule: !publisher.hasCollapsedRelatedQuestionsModule,
  };

  return updateRecord(state, publisherId, updatedPublisherFields);
};

type ContentActionsReducer = (state: Publishers, action: PublishersAction) => Publishers;
export const contentActionsReducer: ContentActionsReducer = (state, action) => {
  switch (action.type) {
    case 'PUBLISHER.POLL_OPTION_CHANGED':
      return changePollOption(state, action.payload);
    case 'PUBLISHER.PRAISED_USERS_ADDED':
      return addPraisedUsersToPublisher(state, action.payload);
    case 'PUBLISHER.PRAISED_USERS_REMOVED':
      return removePraisedUsersFromPublisher(state, action.payload);
    case 'PUBLISHER.PRAISED_USERS_CHANGED':
      return changePraisedUsersFromPublisher(state, action.payload);
    case 'PUBLISHER.PRAISE_ICON_CHANGED':
      return changePraiseIconFromPublisher(state, action.payload);
    case 'PUBLISHER.POST_TYPE_CHANGED':
      return updatePublisherPostType(state, action.payload);
    case 'PUBLISHER.LINK_MODAL_OPENED':
      return openLinkModal(state, action.payload);
    case 'PUBLISHER.LINK_MODAL_CLOSED':
      return closeLinkModal(state, action.payload);
    case 'PUBLISHER.CONTENT_CHANGED':
      return updateContent(state, action.payload);
    case 'PUBLISHER.TITLE_CHANGED':
      return updateTitle(state, action.payload);
    case 'PUBLISHER.SHARED_THREAD_ADDED':
      return updateRecord(state, action.payload.publisherId, { sharedThreadId: action.payload.sharedThreadId });
    case 'PUBLISHER.SHARED_THREAD_REMOVED':
      return updateRecord(state, action.payload.publisherId, { sharedThreadId: undefined });
    case 'PUBLISHER.SHARED_MESSAGE_ADDED':
      return updateRecord(state, action.payload.publisherId, { sharedMessageId: action.payload.sharedMessageId });
    case 'PUBLISHER.SHARED_MESSAGE_REMOVED':
      return updateRecord(state, action.payload.publisherId, { sharedMessageId: undefined });
    case 'THREAD.LOAD_REJECTED':
      return removeFailedSharedThread(state, action.payload);
    case 'NESTED_THREAD.LOAD_REJECTED':
      return removeFailedSharedNestedThread(state, action.payload);
    case 'PUBLISHER.RELATED_QUESTIONS_MODULE_TOGGLED':
      return toggleRelatedQuestionsModule(state, action.payload.publisherId);
    default:
      return state;
  }
};
